import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { apiGet, apiPut, getPlanInfo, ApiError } from '../api/client';
import type { EmailSettings, Merchant, PlanInfo } from '../types';
import { canManagePlan, openPlanManagement, planManagementButtonLabel } from '../utils/billing';
import { formatDate } from '../utils/date';
import { formatFeatureLabel, formatPlanLabel } from '../utils/labels';
import { t } from '../i18n';

interface SettingsProps {
  onError: (error: string | null) => void;
}

export function Settings({ onError }: SettingsProps) {
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [planInfo, setPlanInfo] = useState<PlanInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const [companyName, setCompanyName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [postcode, setPostcode] = useState('');
  const [city, setCity] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [vatId, setVatId] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [commercialRegisterNumber, setCommercialRegisterNumber] = useState('');
  const [defaultLanguage, setDefaultLanguage] = useState('de');
  const [defaultZugferdProfile, setDefaultZugferdProfile] = useState('basic');
  const [invoicePrefix, setInvoicePrefix] = useState('RE-');
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState('1');
  const [currentNextInvoiceNumber, setCurrentNextInvoiceNumber] = useState(1);
  const [automaticMode, setAutomaticMode] = useState('manual');
  const [defaultFormat, setDefaultFormat] = useState('zugferd');
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');
  const [sendInvoices, setSendInvoices] = useState(true);
  const [sendCancellations, setSendCancellations] = useState(true);
  const [sendCreditNotes, setSendCreditNotes] = useState(true);
  const [attachXml, setAttachXml] = useState(false);
  const [showZugferdProfiles, setShowZugferdProfiles] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (success) window.scrollTo(0, 0);
  }, [success]);

  async function loadSettings() {
    try {
      const [merchants, plan] = await Promise.all([
        apiGet<Merchant[]>('/merchants'),
        getPlanInfo(),
      ]);
      const m = merchants?.[0];
      if (m) {
        setMerchant(m);
        setCompanyName(m.company_name || '');
        setLegalName(m.legal_name || '');
        setAddressLine1(m.address_line_1 || '');
        setAddressLine2(m.address_line_2 || '');
        setPostcode(m.postcode || '');
        setCity(m.city || '');
        setCountryCode(m.country_code || '');
        setTaxNumber(m.tax_number || '');
        setVatId(m.vat_id || '');
        setEmail(m.email || '');
        setPhone(m.phone || '');
        setCommercialRegisterNumber(m.commercial_register_number || '');
        setDefaultLanguage(m.default_language || 'de');
        setDefaultZugferdProfile(normalizeZugferdProfile(m.default_zugferd_profile));
        setInvoicePrefix(m.invoice_number_prefix || 'RE-');
        setNextInvoiceNumber(String(m.next_invoice_number || 1));
        setCurrentNextInvoiceNumber(m.next_invoice_number || 1);
        setAutomaticMode(m.automatic_issue_mode || 'manual');
        setDefaultFormat(m.default_structured_format || 'zugferd');
        setIban(m.iban || '');
        setBic(m.bic || '');
        const emailSettings = parseEmailSettings(m.email_settings);
        setSendInvoices(emailSettingValue(emailSettings, 'send_invoices'));
        setSendCancellations(emailSettingValue(emailSettings, 'send_cancellations'));
        setSendCreditNotes(emailSettingValue(emailSettings, 'send_credit_notes'));
        setAttachXml(emailSettingValue(emailSettings, 'attach_xml', false));
      }
      setPlanInfo(plan);
      onError(null);
    } catch {
      onError(t('settings_load_error'));
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!merchant) return;

    const parsedNextInvoiceNumber = Number(nextInvoiceNumber);
    if (!Number.isInteger(parsedNextInvoiceNumber) || parsedNextInvoiceNumber < 1) {
      onError(t('next_invoice_number_positive'));
      return;
    }

    if (parsedNextInvoiceNumber !== currentNextInvoiceNumber && parsedNextInvoiceNumber <= currentNextInvoiceNumber) {
      onError(t('next_invoice_number_must_increase', { number: currentNextInvoiceNumber }));
      return;
    }

    setSaving(true);
    setSuccess(false);
    try {
      await apiPut(`/merchants/${merchant.id}`, {
        company_name: companyName,
        legal_name: legalName,
        address_line_1: addressLine1,
        address_line_2: addressLine2,
        postcode,
        city,
        country_code: countryCode,
        tax_number: taxNumber,
        vat_id: vatId,
        email,
        phone,
        commercial_register_number: commercialRegisterNumber,
        default_language: defaultLanguage,
        default_zugferd_profile: defaultZugferdProfile,
        invoice_number_prefix: invoicePrefix,
        next_invoice_number: parsedNextInvoiceNumber,
        automatic_issue_mode: automaticMode,
        default_structured_format: defaultFormat,
        iban,
        bic,
        email_settings: {
          send_invoices: sendInvoices,
          send_cancellations: sendCancellations,
          send_credit_notes: sendCreditNotes,
          attach_xml: attachXml,
        },
      });
      setSuccess(true);
      onError(null);
    } catch (err) {
      const msg = err instanceof ApiError && err.code === 'invoice_number_already_exists'
        ? t('invoice_number_already_exists')
        : t('settings_save_error');
      onError(msg);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <LoadingSkeleton />;
  }

  const hasXRechnung = planInfo?.features.xrechnung ?? false;
  const hasAutomation = planInfo?.features.automation ?? false;
  const hasEmailDelivery = planInfo?.features.email_delivery ?? false;

  return (
    <div className="page-container">
      <div className="section-header">
        <h1 className="section-title">{t('settings_title')}</h1>
      </div>

      {planInfo && <PlanInfoCard planInfo={planInfo} />}

      {success && (
        <div className="banner banner--success mb-4">
          <span className="banner-content">{t('settings_saved')}</span>
        </div>
      )}

      <form onSubmit={handleSave}>
        <fieldset className="fieldset">
          <legend className="fieldset-legend">{t('company_data')}</legend>
          <Field label={t('company_name')} value={companyName} onChange={setCompanyName} />
          <Field label={t('legal_name')} value={legalName} onChange={setLegalName} />
          <Field label={t('address')} value={addressLine1} onChange={setAddressLine1} />
          <Field label={t('address_extra')} value={addressLine2} onChange={setAddressLine2} />
          <div className="form-row">
            <Field label={t('postcode')} value={postcode} onChange={setPostcode} />
            <Field label={t('city')} value={city} onChange={setCity} />
          </div>
          <Field label={t('country_code')} value={countryCode} onChange={setCountryCode} placeholder={t('country_code_placeholder')} />
          <Field label={t('tax_number')} value={taxNumber} onChange={setTaxNumber} />
          <Field label={t('vat_id')} value={vatId} onChange={setVatId} />
          <Field label={t('email_label')} value={email} onChange={setEmail} type="email" />
          <Field label={t('phone')} value={phone} onChange={setPhone} />
          <Field label={t('commercial_register')} value={commercialRegisterNumber} onChange={setCommercialRegisterNumber} />
        </fieldset>

        <fieldset className="fieldset">
          <legend className="fieldset-legend">{t('bank_data')}</legend>
          <Field label={t('iban')} value={iban} onChange={setIban} placeholder={t('iban_placeholder')} />
          <Field label={t('bic')} value={bic} onChange={setBic} placeholder={t('bic_placeholder')} />
        </fieldset>

        <fieldset className="fieldset">
          <legend className="fieldset-legend">{t('invoice_settings')}</legend>
          <Field label={t('invoice_prefix')} value={invoicePrefix} onChange={setInvoicePrefix} />
          <Field
            label={t('next_invoice_number')}
            value={nextInvoiceNumber}
            onChange={setNextInvoiceNumber}
            type="number"
            min={1}
            step={1}
          />
          <SelectField
            label={t('auto_issue')}
            value={automaticMode}
            onChange={setAutomaticMode}
            disabled={!hasAutomation}
            lockedMessage={!hasAutomation ? t('requires_professional') : undefined}
            options={[
              { label: t('auto_manual'), value: 'manual' },
              { label: t('auto_draft'), value: 'draft' },
              { label: t('auto_after_payment'), value: 'after_payment' },
              { label: t('auto_after_fulfillment'), value: 'after_fulfillment' },
            ]}
          />
          <SelectField
            label={t('structured_format')}
            value={defaultFormat}
            onChange={setDefaultFormat}
            disabled={!hasXRechnung}
            lockedMessage={!hasXRechnung ? t('requires_starter') : undefined}
            options={[
              { label: t('zugferd'), value: 'zugferd' },
              { label: t('xrechnung'), value: 'xrechnung' },
            ]}
          />
          <SelectField
            label={t('default_zugferd_profile')}
            value={defaultFormat === 'xrechnung' ? '' : defaultZugferdProfile}
            onChange={setDefaultZugferdProfile}
            disabled={defaultFormat === 'xrechnung'}
            lockedMessage={defaultFormat === 'xrechnung' ? t('zugferd_profile_only') : undefined}
            labelAction={(
              <div className="profile-info">
                <button
                  type="button"
                  className="profile-info-button"
                  aria-label={t('zugferd_profile_info_button')}
                  aria-expanded={showZugferdProfiles}
                  onClick={() => setShowZugferdProfiles((visible) => !visible)}
                >
                  i
                </button>
                {showZugferdProfiles && <ZugferdProfileInfo />}
              </div>
            )}
            options={[
              { label: t('profile_minimum'), value: 'minimum' },
              { label: t('profile_basicwl'), value: 'basicwl' },
              { label: t('profile_basic'), value: 'basic' },
              { label: t('profile_en16931'), value: 'en16931' },
              { label: t('profile_extended'), value: 'extended' },
              { label: t('xrechnung'), value: 'xrechnung' },
            ]}
          />
          <SelectField
            label={t('default_language')}
            value={defaultLanguage}
            onChange={setDefaultLanguage}
            options={[
              { label: t('lang_german'), value: 'de' },
              { label: t('lang_english'), value: 'en' },
            ]}
          />
          <p className="form-hint">{t('default_language_hint')}</p>
        </fieldset>

        <fieldset className="fieldset">
          <legend className="fieldset-legend">{t('email_delivery_settings')}</legend>
          <CheckboxField
            label={t('send_invoices_to_customer')}
            checked={sendInvoices}
            onChange={setSendInvoices}
            disabled={!hasEmailDelivery}
          />
          <CheckboxField
            label={t('send_cancellations_to_customer')}
            checked={sendCancellations}
            onChange={setSendCancellations}
            disabled={!hasEmailDelivery}
          />
          <CheckboxField
            label={t('send_credit_notes_to_customer')}
            checked={sendCreditNotes}
            onChange={setSendCreditNotes}
            disabled={!hasEmailDelivery}
          />
          <CheckboxField
            label={t('attach_pdf_to_email')}
            checked={true}
            onChange={() => undefined}
            disabled={true}
          />
          <CheckboxField
            label={t('attach_xml_to_email')}
            checked={attachXml}
            onChange={setAttachXml}
            disabled={!hasEmailDelivery}
          />
          {!hasEmailDelivery && <p className="form-hint">{t('email_delivery_requires_starter')}</p>}
          <p className="form-hint">{t('customer_email_delivery_hint')}</p>
        </fieldset>

        <button type="submit" disabled={saving} className="btn btn--primary btn--lg">
          {saving ? t('saving') : t('save_settings')}
        </button>
      </form>
    </div>
  );
}

function parseEmailSettings(value: Merchant['email_settings']): EmailSettings {
  if (!value) return {};
  if (typeof value !== 'string') return value;

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function emailSettingValue(
  settings: EmailSettings,
  key: keyof Pick<EmailSettings, 'send_invoices' | 'send_cancellations' | 'send_credit_notes' | 'attach_xml'>,
  defaultValue = true,
): boolean {
  if (typeof settings[key] === 'boolean') return Boolean(settings[key]);
  if (settings.auto_send === false) return false;

  return defaultValue;
}

function normalizeZugferdProfile(value?: string | null): string {
  const normalized = (value || 'basic').toLowerCase().replace(/[_\s-]/g, '');
  if (normalized === 'comfort') return 'basic';
  if (normalized === 'basicwl') return 'basicwl';
  if (['minimum', 'basic', 'en16931', 'extended', 'xrechnung'].includes(normalized)) {
    return normalized;
  }
  return 'basic';
}

function ZugferdProfileInfo() {
  const profiles = [
    { name: 'MINIMUM', status: '✓', notes: t('profile_info_minimum') },
    { name: 'BASICWL', status: '✓', notes: t('profile_info_basicwl') },
    { name: 'BASIC', status: '✓', notes: t('profile_info_basic') },
    { name: 'EN16931', status: '✓', notes: t('profile_info_en16931') },
    { name: 'EXTENDED', status: '✓', notes: t('profile_info_extended') },
    { name: 'XRECHNUNG', status: '✓', notes: t('profile_info_xrechnung') },
  ];

  return (
    <div className="profile-info-card" role="dialog" aria-label={t('zugferd_profile_info_title')}>
      <div className="profile-info-title">{t('zugferd_profile_info_title')}</div>
      <div className="profile-info-copy">{t('zugferd_profile_info_copy')}</div>
      <div className="profile-info-table">
        <div className="profile-info-row profile-info-row--header">
          <span>{t('profile_info_col_profile')}</span>
          <span>{t('profile_info_col_status')}</span>
          <span>{t('profile_info_col_notes')}</span>
        </div>
        {profiles.map((profile) => (
          <div key={profile.name} className="profile-info-row">
            <span className="profile-info-name">{profile.name}</span>
            <span className="profile-info-status">{profile.status}</span>
            <span>{profile.notes}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlanInfoCard({ planInfo }: { planInfo: PlanInfo }) {
  const planBadgeClass = planInfo.handle === 'professional' ? 'success' : planInfo.handle === 'starter' ? 'info' : 'neutral';

  return (
    <div className="card mb-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="text-sm text-secondary mb-2">{t('current_plan')}</div>
          <span className={`badge badge--${planBadgeClass}`} style={{ fontSize: 'var(--font-size-md)', padding: '4px 12px' }}>
            {formatPlanLabel(planInfo.label)}
          </span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="text-sm fw-medium">
            {planInfo.documents_used} / {planInfo.document_limit} {t('documents_used_label')}
          </div>
          <div className="text-disabled mt-2" style={{ fontSize: 'var(--font-size-xs)' }}>
            {t('reset_on')} {formatDate(planInfo.reset_date)}
          </div>
          {(planInfo.can_manage_plan ?? Boolean(planInfo.pricing_url)) && (
            <button
              type="button"
              onClick={() => openPlanManagement(planInfo)}
              disabled={!canManagePlan(planInfo)}
              title={!canManagePlan(planInfo) ? t('manage_plan_tooltip') : undefined}
              className="btn btn--primary btn--sm mt-4"
            >
              {planManagementButtonLabel()}
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
        {Object.entries(planInfo.features).map(([key, enabled]) => (
          <span
            key={key}
            className={`badge badge--${enabled ? 'success' : 'neutral'}`}
          >
            {enabled ? '✓' : '○'} {formatFeatureLabel(key)}
          </span>
        ))}
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="page-container">
      <div className="skeleton" style={{ height: 32, width: 100, marginBottom: 'var(--space-6)' }} />
      {[1, 2, 3].map(i => (
        <div key={i} className="skeleton" style={{ height: 180, marginBottom: 'var(--space-4)' }} />
      ))}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, min, step }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string; min?: number; step?: number;
}) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        min={min}
        step={step}
        onChange={(e) => onChange(e.target.value)}
        className="form-input"
      />
    </div>
  );
}

function CheckboxField({ label, checked, onChange, disabled }: {
  label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean;
}) {
  return (
    <label
      className="form-label"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 18, height: 18 }}
      />
      {label}
    </label>
  );
}

function SelectField({ label, value, onChange, options, disabled, lockedMessage, labelAction }: {
  label: string; value: string; onChange: (v: string) => void; options: { label: string; value: string }[];
  disabled?: boolean; lockedMessage?: string; labelAction?: ReactNode;
}) {
  return (
    <div className="form-group">
      <div className="form-label">
        <span className="form-label-row">
          <span>
            {label}
            {lockedMessage && <span className="fieldset-hint">{lockedMessage}</span>}
          </span>
          {labelAction}
        </span>
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="form-input form-select"
        style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}
